import { formatDefaultDateTime } from "./Formatter";
import { suggestionPopups } from "./utilHelpers";

/**
 *
 * @param {String} text - The text to be displayed.
 * @param {Object} colors - The colors object containing the color classes and their corresponding values.
 * @param {String} notFoundDefault - The default color class to be used.
 * @param {String} textEmptyDefault - The default text to be displayed if the text is empty.
 * @returns {JSX.Element} The span element with the color class.
 *
 * @example
 * const colors = [
 *    {
 *      colorClass: "text-green-500",
 *      forValues: ["Active", "Available"]
 *   },
 *   {
 *     colorClass: "text-red-500",
 *     forValues: ["Inactive", "Unavailable"]
 *   }];
 *
 * const span = getSpanWithColor("Active", colors, notFoundDefault);
 *
 */
export const getSpanWithColor = (
    text,
    colors = defaultStatusColors,
    notFoundDefault = "text-gray-200",
    textEmptyDefault = "--"
) => {
    try {
        if (typeof text === "boolean") {
            text = text ? "Yes" : "No";
        }
        if (!text || text === "" || text === undefined || text === null) {
            return <span className={notFoundDefault}>{textEmptyDefault}</span>;
        }

        let colorValue = notFoundDefault;
        let found = false;
        for (const color of colors) {
            for (const value of color.forValues) {
                if (value?.toLowerCase() === text?.toLowerCase()) {
                    found = true;
                    colorValue = color.colorClass;
                    break;
                }
            }
            if (found) break;
        }

        return <span className={colorValue}>{text}</span>;
    } catch (error) {
        console.error("getSpanWithColor", text);
        return <span className={notFoundDefault}>{textEmptyDefault}</span>;
    }
};

export const getOpenHoursSpan = text => {
    if (text === "" || text === undefined || text === null || typeof text !== "boolean") {
        return <span className="text-gray-100">--</span>;
    }

    let colorValue = "text-orange-500";
    if (text === true) {
        text = "Open";
        colorValue = "text-green-500";
    } else {
        text = "Closed";
    }

    return <span className={colorValue}>{text}</span>;
};

export const REORDER_STATUS = {
    NEW: "New",
    ONGOING: "Ongoing",
    ACCEPTED_NOT_YET_SENT: "AcceptedByRetailBrand",
    SENT: "Sent",
    DECLINED_BY_RETAIL_BRAND: "DeclinedByRetailBrand",
    DECLINED: "Declined",
    DECLINED_BY_LABEL_BRAND: "DeclinedByLabelBrand",
    ACCEPTED_BY_LABEL_BRAND: "AcceptedByLabelBrand",
    PROCESSING: "Processing",
    PROCESSED: "Processed",
    EDITED: "Edited",
    EDIT_ACCEPTED: "EditAccepted",
    EDIT_DECLINED: "EditDeclined",
    EDIT_BY: "Edited by brand",
    IMPORTED: "Imported",
    SHARED: "Shared",
    EXPORTED_BY_RETAIL_BRAND: "ExportedByRetailBrand",
    DELIVERED: "Delivered"
};

export const getActionLabelByReorderStatus = status => {
    let label = "Approved by:";

    if (status === REORDER_STATUS.NEW) label = "Generated by:";
    else if (status === REORDER_STATUS.ONGOING) label = "Last checked by:";
    else if (status === REORDER_STATUS.ACCEPTED_NOT_YET_SENT) label = "Accepted by:";
    else if (status === REORDER_STATUS.SENT) label = "Sent by:";
    else if (
        status === REORDER_STATUS.DECLINED_BY_RETAIL_BRAND ||
        status === REORDER_STATUS.DECLINED_BY_LABEL_BRAND ||
        status === REORDER_STATUS.DECLINED
    )
        label = "Declined by:";
    else if (status === REORDER_STATUS.ACCEPTED_BY_LABEL_BRAND) label = "Accepted by label:";
    else if (status === REORDER_STATUS.EDITED) label = "Edited by:";
    else if (status === REORDER_STATUS.EDIT_ACCEPTED) label = "Edit accepted by:";
    else if (status === REORDER_STATUS.EDIT_DECLINED) label = "Edit declined by:";
    else if (status === REORDER_STATUS.DELIVERED) label = "Delivered by:";
    else if (status === REORDER_STATUS.IMPORTED) label = "Imported by:";
    else if (status === REORDER_STATUS.SHARED) label = "Shared by:";
    else if (status === REORDER_STATUS.EXPORTED_BY_RETAIL_BRAND) label = "Exported by:";
    else if (status === REORDER_STATUS.PROCESSED) label = "Processed by:";

    return { label };
};

export const defaultStatusColors = [
    {
        colorClass: "text-blue-600",
        forValues: ["Active", "Fulfilling", "Connected", "Affiliated", "Accepted", "Processed"]
    },
    {
        colorClass: "text-green-500",
        forValues: [
            "Available",
            "Exported to POS",
            "Exported",
            "Invited",
            "Invite-Resent",
            "Completed",
            "Yes",
            "Enrolling",
            "Enrolled",
            "Pre-Enrolled",
            "Activating",
            "Connecting",
            "Integrated",
            "Ongoing",
            "Sent",
            "New",
            "Delivered",
            "Shared",
            "Imported",
            "Processing"
        ]
    },
    {
        colorClass: "text-orange-500",
        forValues: [
            "Suspended",
            "Pending",
            "N/A",
            "Request",
            "Requested",
            "Not affiliated",
            "Suspending",
            "Removing",
            "Not fulfilling",
            "Not active",
            "Accepted, not yet sent",
            "Edited by brand",
            "AcceptedByRetailBrand",
            "Edited",
            "EditAccepted",
            "Sent for approval"
        ]
    },
    {
        colorClass: "text-red-500",
        forValues: [
            "Inactive",
            "Unavailable",
            "Not connected",
            "Failed",
            "Cancelled",
            "Declined",
            "No",
            "Voided",
            "Not integrated",
            "DeclinedByRetailBrand",
            "DeclinedByLabelBrand",
            "EditDeclined",
            "Disconnected"
        ]
    },
    {
        colorClass: "text-gray-300",
        forValues: ["--"]
    }
];

export const accountStatusColors = [
    {
        colorClass: "text-blue-500",
        forValues: ["Active"]
    },
    {
        colorClass: "text-green-500",
        forValues: ["Enrolled", "Pre-Enrolled", "Enrolling", "Activating"]
    }
];

export const getStatusValue = (row, statusFromPath) => {
    const fromPath = statusFromPath ?? "status";
    const status = getArrayFieldValue(row, fromPath.split(".") || "Unknown");
    //console.log("status", status);
    return status;
};

const getArrayFieldValue = (row, array) => {
    if (!Array.isArray(array)) return "Unknown";

    let res = row;
    for (let i = 0; i < array.length; i++) {
        res = res?.[array[i]];
    }
    return res;
};

export const getStatusAndText = status => {
    let highlightText = "";
    let background_color = "";

    if (status === REORDER_STATUS.NEW) {
        highlightText = "NEW";
        background_color = "rgba(0, 136, 0, 1)";
    } else if (status === REORDER_STATUS.ONGOING) {
        highlightText = "KEEP GOING";
        background_color = "rgba(0, 136, 0, 1)";
    } else if (status === REORDER_STATUS.SENT) {
        highlightText = "SENT";
        background_color = "rgba(0, 136, 0, 1)";
    } else if (status === REORDER_STATUS.ACCEPTED_NOT_YET_SENT) {
        highlightText = "NOT YET SENT";
        background_color = "rgba(255, 153, 0, 1)";
    } else if (status === REORDER_STATUS.ACCEPTED_BY_LABEL_BRAND) {
        highlightText = "ACCEPTED";
        background_color = "rgba(0, 136, 0, 1)";
    } else if (
        status === REORDER_STATUS.DECLINED_BY_RETAIL_BRAND ||
        status === REORDER_STATUS.DECLINED_BY_LABEL_BRAND ||
        status === REORDER_STATUS.EDIT_DECLINED
    ) {
        highlightText = "DECLINED";
        background_color = "#FF3636";
    } else if (status === REORDER_STATUS.EDIT_BY || status === REORDER_STATUS.EDITED) {
        highlightText = "EDITED BY BRAND";
        background_color = "rgba(255, 98, 0, 1)";
    } else if (status === REORDER_STATUS.EDITED) {
        highlightText = "SENT FOR APPROVAL";
        background_color = "rgba(211, 126, 0, 1)";
    } else if (status === REORDER_STATUS.DELIVERED) {
        highlightText = "DELIVERED";
        background_color = "rgba(0, 136, 0, 1)";
    } else if (status === REORDER_STATUS.IMPORTED) {
        highlightText = "IMPORTED";
        background_color = "rgba(0, 136, 0, 1)";
    } else if (status === REORDER_STATUS.SHARED) {
        highlightText = "SHARED";
        background_color = "rgba(0, 136, 0, 1)";
    } else if (status === REORDER_STATUS.EXPORTED_BY_RETAIL_BRAND) {
        highlightText = "EXPORTED";
        background_color = "rgba(0, 136, 0, 1)";
    } else if (status === REORDER_STATUS.PROCESSED) {
        highlightText = "PROCESSED";
        background_color = "rgba(0, 96, 204, 1)";
    }

    return { highlightText, background_color };
};

export const getStatusAndLabel = status => {
    let _status;
    if (status === "DeclinedByRetailBrand") {
        _status = "Declined";
    } else if (status === "AcceptedByRetailBrand") {
        _status = "Accepted, not yet sent";
    } else if (status === "DeclinedByLabelBrand") {
        _status = "Declined";
    } else if (status === "AcceptedByLabelBrand") {
        _status = "Processing";
    } else if (status === "Edited") {
        _status = "Sent for approval";
    } else {
        _status = status;
    }
    return { _status };
};
export const statusOptions = [
    { name: "New", value: "New" },
    { name: "processing", value: "processing" },
    { name: "Declined by label brand", value: "DeclinedByRetailBrand" },
    { name: "Processed", value: "Processed" }
];

export const getTitleAndDescription = ({ status, reOrderNumber, createdAt, userName }) => {
    //todo:createdAt is not correct here need to change this

    let title;
    let description;
    let suggestionText;
    let buttonText;

    if (status === REORDER_STATUS.DECLINED_BY_RETAIL_BRAND) {
        title = `Reorder no:${reOrderNumber}`;
        description = `This order was DECLINED by ${userName} on ${formatDefaultDateTime(createdAt)}`;
        suggestionText = "DECLINED";
        buttonText = "CLOSE";
    } else if (status === REORDER_STATUS.ONGOING) {
        title = "One or more variations of this item are running fast!";
        description = "To ensure you keep doing good business, BOtag AI suggests the following reorder:";
        suggestionText =
            "Suggested quantities are based on this item’s performance in your stores. You can edit to adjust.";
    } else if (status === REORDER_STATUS.SENT) {
        title = `Reorder no:${reOrderNumber}`;
        description = `This order was SENT by ${userName} on ${formatDefaultDateTime(createdAt)}`;
        suggestionText = "SENT";
        buttonText = "CLOSE";
    } else if (status === REORDER_STATUS.ACCEPTED_NOT_YET_SENT) {
        title = `Reorder no:${reOrderNumber}`;
        description = `This order was ACCEPTED by ${userName} on ${formatDefaultDateTime(createdAt)}`;
        suggestionText = "ACCEPTED, READY TO SEND";
        buttonText = "SEND";
    } else {
        title = "One or more variations of this item are running fast!";
        description = "To ensure you keep doing good business, BOtag AI suggests the following reorder:";
        suggestionText =
            "Suggested quantities are based on this item’s performance in your stores. You can edit to adjust.";
        buttonText = "";
    }
    return { title, description, suggestionText, buttonText };
};
